import { logEvent as logAnalyticsEvent } from '../analytics';
import {
    EventsStream,
    LiveChatConnectionStatus,
    OnLiveChatAgentConnectedEvent,
    OnLiveChatClosedEvent,
    OnLiveChatErrorEvent,
    OnLiveChatMessageEvent,
    OnLiveChatOpenedEvent,
    OnLiveChatOpeningEvent,
} from '../liveChatService';
import {
    getLocalStorageItem,
    setLocalStorageItem,
} from '../utils/localStorage';

export type LiveChatAnalyticsDisposer = () => void;

const LIVE_CHAT_ANALYTICS_STORAGE_KEY = 'lca';
const LIVE_CHAT_ANALYTICS_TTL = 30 * 60 * 1000;

export interface LiveChatAnalyticsState {
    userMessagesCount: number;
    agentMessagesCount: number;
    agentStartedConversation: boolean;
    transferInitiated: boolean;
    startConversationTimestamp: number;
    liveChatEnded: boolean;
}

export class LiveChatAnalytics {
    liveChatServiceEventStream: EventsStream;
    liveChatAnalyticsEventBaseName: string;
    liveChatAnalyticsEventFlow: string;
    expectedConnectionInstant: number; // ms
    token: string;
    liveChatTenantName: string;
    state: LiveChatAnalyticsState;

    constructor(
        liveChatServiceEventStream: EventsStream,
        liveChatAnalyticsEventBaseName: string,
        liveChatAnalyticsEventFlow: string,
        expectedConnectionInstant: number, // ms
        token: string,
        liveChatTenantName: string
    ) {
        this.liveChatServiceEventStream = liveChatServiceEventStream;
        this.liveChatAnalyticsEventBaseName = liveChatAnalyticsEventBaseName;
        this.liveChatAnalyticsEventFlow = liveChatAnalyticsEventFlow;
        this.expectedConnectionInstant = expectedConnectionInstant;
        this.token = token;
        this.liveChatTenantName = liveChatTenantName;

        const lca = getLocalStorageItem(LIVE_CHAT_ANALYTICS_STORAGE_KEY) as {
            [key: string]: LiveChatAnalyticsState;
        };
        if (lca && token in lca) {
            this.state = lca[token] as LiveChatAnalyticsState;
        } else {
            this.state = {
                userMessagesCount: 0,
                agentMessagesCount: 0,
                agentStartedConversation: false,
                transferInitiated: false,
                startConversationTimestamp: 0,
                liveChatEnded: false,
            };
        }
    }

    setState(value: Partial<LiveChatAnalyticsState>) {
        this.state = Object.assign(this.state, value);
        let lca = getLocalStorageItem(LIVE_CHAT_ANALYTICS_STORAGE_KEY) as {
            [key: string]: LiveChatAnalyticsState;
        };
        if (!lca) {
            lca = {};
        }
        lca[this.token] = this.state;
        setLocalStorageItem(
            LIVE_CHAT_ANALYTICS_STORAGE_KEY,
            lca,
            LIVE_CHAT_ANALYTICS_TTL
        );
    }

    track(): LiveChatAnalyticsDisposer {
        // If this live chat ended, exit immediately
        if (this.state.liveChatEnded) {
            return () => undefined;
        }

        if (!this.state.transferInitiated) {
            logAnalyticsEvent(
                `${this.liveChatAnalyticsEventBaseName} - Chat module shown`,
                {
                    flow: this.liveChatAnalyticsEventFlow,
                    live_chat_tenant_name: this.liveChatTenantName,
                }
            );
        }
        let userWaitingInterval: number | undefined = undefined;
        let userChattingInterval: number | undefined = undefined;

        const waitTimeMinutes = (): number => {
            return Math.max(
                0,
                Math.ceil((this.expectedConnectionInstant - Date.now()) / 60000)
            );
        };

        const setupUserChattingInterval = () => {
            if (userChattingInterval) {
                return;
            }
            userChattingInterval = window.setInterval(() => {
                logAnalyticsEvent(
                    `${this.liveChatAnalyticsEventBaseName} - Chat session continued`,
                    {
                        flow: this.liveChatAnalyticsEventFlow,
                        live_chat_tenant_name: this.liveChatTenantName,
                        chat_session_length: Math.ceil(
                            (Date.now() -
                                this.state.startConversationTimestamp) /
                                60000
                        ),
                    }
                );
            }, 180000); // every 3mn
        };

        if (this.state.agentStartedConversation) {
            setupUserChattingInterval();
        }

        const listener = this.liveChatServiceEventStream.listen((event) => {
            if (
                event instanceof OnLiveChatOpeningEvent &&
                !this.state.agentStartedConversation
            ) {
                this.setState({ transferInitiated: true });
                logAnalyticsEvent(
                    `${this.liveChatAnalyticsEventBaseName} - Transfer Initiated`,
                    {
                        flow: this.liveChatAnalyticsEventFlow,
                        live_chat_tenant_name: this.liveChatTenantName,
                        wait_time_shown: waitTimeMinutes(),
                    }
                );
            } else if (
                event instanceof OnLiveChatOpenedEvent &&
                !this.state.agentStartedConversation
            ) {
                logAnalyticsEvent(
                    `${this.liveChatAnalyticsEventBaseName} - Waiting for Live Agent`,
                    {
                        flow: this.liveChatAnalyticsEventFlow,
                        live_chat_tenant_name: this.liveChatTenantName,
                        wait_time_shown: waitTimeMinutes(),
                    }
                );

                userWaitingInterval = window.setInterval(() => {
                    logAnalyticsEvent(
                        `${this.liveChatAnalyticsEventBaseName} - Waiting for Live Agent - Waiting`,
                        {
                            flow: this.liveChatAnalyticsEventFlow,
                            live_chat_tenant_name: this.liveChatTenantName,
                            wait_time_shown: waitTimeMinutes(),
                        }
                    );
                }, 300000); // every 5mn
            } else if (
                event instanceof OnLiveChatAgentConnectedEvent &&
                !this.state.agentStartedConversation
            ) {
                logAnalyticsEvent(
                    `${this.liveChatAnalyticsEventBaseName} - Live Agent is ready`,
                    {
                        flow: this.liveChatAnalyticsEventFlow,
                        live_chat_tenant_name: this.liveChatTenantName,
                    }
                );
            } else if (event instanceof OnLiveChatMessageEvent) {
                if (
                    event.isUserMessage &&
                    event.message.body.direction === 'Outbound'
                ) {
                    if (!this.state.agentStartedConversation) {
                        this.setState({
                            agentMessagesCount: 1,
                            agentStartedConversation: true,
                            startConversationTimestamp: Date.now(),
                        });

                        if (userWaitingInterval !== undefined) {
                            clearInterval(userWaitingInterval);
                            userWaitingInterval = undefined;
                        }

                        logAnalyticsEvent(
                            `${this.liveChatAnalyticsEventBaseName} - Chat session started`,
                            {
                                flow: this.liveChatAnalyticsEventFlow,
                                live_chat_tenant_name: this.liveChatTenantName,
                            }
                        );

                        setupUserChattingInterval();
                    } else {
                        this.setState({
                            agentMessagesCount:
                                this.state.agentMessagesCount + 1,
                        });
                    }
                } else if (
                    event.isUserMessage &&
                    event.message.body.direction === 'Inbound' &&
                    !event.isSystemMessage
                ) {
                    this.setState({
                        userMessagesCount: this.state.userMessagesCount + 1,
                    });
                }
            } else if (event instanceof OnLiveChatClosedEvent) {
                if (userWaitingInterval !== undefined) {
                    clearInterval(userWaitingInterval);
                    userWaitingInterval = undefined;
                }

                if (userChattingInterval !== undefined) {
                    clearInterval(userChattingInterval);
                    userChattingInterval = undefined;
                }

                logAnalyticsEvent(
                    `${this.liveChatAnalyticsEventBaseName} - Chat session ended`,
                    {
                        flow: this.liveChatAnalyticsEventFlow,
                        live_chat_tenant_name: this.liveChatTenantName,
                        chat_length:
                            this.state.userMessagesCount +
                            this.state.agentMessagesCount,
                        chat_time: Math.ceil(
                            (Date.now() -
                                this.state.startConversationTimestamp) /
                                1000
                        ),
                        user_messages: this.state.userMessagesCount,
                    }
                );
                this.setState({ liveChatEnded: true });
            } else if (event instanceof OnLiveChatErrorEvent) {
                if (
                    event.connectionStatus ===
                    LiveChatConnectionStatus.connecting
                ) {
                    logAnalyticsEvent(
                        `${this.liveChatAnalyticsEventBaseName} - Error`,
                        {
                            event: `${this.liveChatAnalyticsEventBaseName} - Waiting for Live Agent`,
                            live_chat_tenant_name: this.liveChatTenantName,
                        }
                    );
                } else if (
                    event.connectionStatus ===
                    LiveChatConnectionStatus.serviceConnected
                ) {
                    logAnalyticsEvent(
                        `${this.liveChatAnalyticsEventBaseName} - Error`,
                        {
                            event: `${this.liveChatAnalyticsEventBaseName} - Waiting for Live Agent`,
                            live_chat_tenant_name: this.liveChatTenantName,
                        }
                    );
                } else if (
                    event.connectionStatus ===
                    LiveChatConnectionStatus.agentConnected
                ) {
                    logAnalyticsEvent(
                        `${this.liveChatAnalyticsEventBaseName} - Error`,
                        {
                            event: `${this.liveChatAnalyticsEventBaseName} - Live Agent is ready`,
                            live_chat_tenant_name: this.liveChatTenantName,
                        }
                    );
                } else {
                    logAnalyticsEvent(
                        `${this.liveChatAnalyticsEventBaseName} - Error`,
                        {
                            event: `${this.liveChatAnalyticsEventBaseName} - Disconnected`,
                            live_chat_tenant_name: this.liveChatTenantName,
                        }
                    );
                }
            }
        });

        return () => {
            if (userWaitingInterval !== undefined) {
                clearInterval(userWaitingInterval);
                userWaitingInterval = undefined;
            }

            if (userChattingInterval !== undefined) {
                clearInterval(userChattingInterval);
                userChattingInterval = undefined;
            }

            listener.dispose();
        };
    }
}

export const addLinkClickEvents = (
    eventName: string,
    liveChatTenant: string
) => {
    const botMessages = Array.from(
        document.querySelectorAll('[data-lc-msg="true"]')
    );
    for (const botMessage of botMessages) {
        const links = Array.from(botMessage.querySelectorAll('a'));
        for (const link of links) {
            link.addEventListener(
                'click',
                analyticsLinkClickHandler(eventName, liveChatTenant)
            );
        }
    }
};

const analyticsLinkClickHandler =
    (liveChatAnalyticsEventBaseName: string, liveChatTenant: string) =>
    (e: MouseEvent) => {
        const target = e.target as HTMLAnchorElement;
        let url = target.href;
        if (url.indexOf('?') > 0) {
            url = url.substring(0, url.indexOf('?'));
        }
        const textContent = target.textContent;
        logAnalyticsEvent(
            `${liveChatAnalyticsEventBaseName} - External link selected`,
            {
                live_chat_tenant_name: liveChatTenant,
                link_url: url || '',
                link_text: textContent || '',
            }
        );
    };
